import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import './DailySummary.css';

const STATUS_TYPES = new Set(['마감', '연차', '반차', '경조']);

function DailySummary() {
    const { year, month, day } = useParams();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const captureRef = useRef(null);

    const date = new Date(year, month - 1, day);
    const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayOfWeek = date.toLocaleDateString('ko-KR', { weekday: 'long' });

    useEffect(() => {
        axios.get(`/api/schedules/summary/${year}/${month}/${day}`)
            .then((response) => {
                setSummary(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching daily summary:', error);
                setLoading(false);
            });
    }, [year, month, day]);

    const handleDownload = async () => {
        const element = captureRef.current;
        if (!element) return;

        const canvas = await html2canvas(element, {
            useCORS: true,
            backgroundColor: '#ffffff',
            scale: 2,
            scrollX: 0,
            scrollY: -window.scrollY,
            width: element.scrollWidth,
            height: element.scrollHeight,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight
        });

        const data = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = data;
        link.download = `${formattedDate} ${dayOfWeek} 일일스케줄.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return <div className="summary-container"><h2>Loading...</h2></div>;
    }

    if (!summary) {
        return <div className="summary-container"><h2>Failed to load data.</h2></div>;
    }

    const renderEmployeeName = (employee) => {
        const rawStatus = typeof employee.statusLabel === 'string' && employee.statusLabel.trim()
            ? employee.statusLabel.trim()
            : typeof employee.workType === 'string' && employee.workType.trim()
                ? employee.workType.trim()
                : null;
        const statusLabel = rawStatus && STATUS_TYPES.has(rawStatus) ? rawStatus : null;
        const displayName = [employee.position, employee.name].filter(Boolean).join(' ');

        return (
            <div key={employee.id} className="employee-name-row">
                <span className="employee-name-text">{displayName}</span>
                {statusLabel && (
                    <small className="employee-status-tag">{`(${statusLabel})`}</small>
                )}
            </div>
        );
    };

    const processCombinedEmployees = (onWork, offWork) => {
        const combinedMap = new Map();
        const orderedEmployees = [...onWork, ...offWork].sort((a, b) => {
            const orderA = typeof a.displayOrder === 'number' ? a.displayOrder : Number.MAX_SAFE_INTEGER;
            const orderB = typeof b.displayOrder === 'number' ? b.displayOrder : Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
        });

        orderedEmployees.forEach((emp) => {
            const key = `${emp.department}-${emp.part}-${emp.duty}`;
            if (!combinedMap.has(key)) {
                combinedMap.set(key, {
                    department: emp.department,
                    part: emp.part,
                    duty: emp.duty,
                    onWorkList: [],
                    offWorkList: []
                });
            }
            const targetList = offWork.some((offEmp) => offEmp.id === emp.id) ? 'offWorkList' : 'onWorkList';
            combinedMap.get(key)[targetList].push(emp);
        });

        const groups = Array.from(combinedMap.values());
        const processed = [];

        for (let i = 0; i < groups.length; i++) {
            const group = groups[i];
            const row = { ...group, showDept: false, showPart: false, showDuty: false };

            if (i === 0 || group.department !== groups[i - 1].department) {
                row.showDept = true;
                let rowSpan = 1;
                for (let j = i + 1; j < groups.length; j++) {
                    if (groups[j].department === group.department) rowSpan++;
                    else break;
                }
                row.deptRowSpan = rowSpan;
            }

            if (i === 0 || group.department !== groups[i - 1].department || group.part !== groups[i - 1].part) {
                row.showPart = true;
                let rowSpan = 1;
                for (let j = i + 1; j < groups.length; j++) {
                    if (groups[j].department === group.department && groups[j].part === group.part) rowSpan++;
                    else break;
                }
                row.partRowSpan = rowSpan;
            }

            if (
                i === 0 ||
                group.department !== groups[i - 1].department ||
                group.part !== groups[i - 1].part ||
                group.duty !== groups[i - 1].duty
            ) {
                row.showDuty = true;
                let rowSpan = 1;
                for (let j = i + 1; j < groups.length; j++) {
                    if (
                        groups[j].department === group.department &&
                        groups[j].part === group.part &&
                        groups[j].duty === group.duty
                    ) rowSpan++;
                    else break;
                }
                row.dutyRowSpan = rowSpan;
            }

            processed.push(row);
        }

        return processed;
    };

    const processedCombinedEmployees = processCombinedEmployees(summary.onWork, summary.offWork);
    const headers = ['부서', '파트', '직무', '근무자', '휴무자'];

    return (
        <div className="summary-container">
            <header className="summary-header">
                <div className="summary-header-spacer"></div>
                <button onClick={handleDownload} className="download-button">다운로드</button>
            </header>

            <div className="summary-capture" ref={captureRef}>
                <div className="summary-capture-title">
                    <h1>{`일일 스케줄 (${formattedDate})`}</h1>
                </div>

                <div className="summary-table-wrapper">
                    <table className="summary-table">
                        <thead>
                            <tr>
                                {headers.map((header) => (
                                    <th key={header}>{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {processedCombinedEmployees.length > 0 ? (
                                processedCombinedEmployees.map((group, index) => (
                                    <tr key={index}>
                                        {group.showDept && <td rowSpan={group.deptRowSpan}>{group.department}</td>}
                                        {group.showPart && <td rowSpan={group.partRowSpan}>{group.part}</td>}
                                        {group.showDuty && <td rowSpan={group.dutyRowSpan}>{group.duty}</td>}
                                        <td>
                                            {group.onWorkList.length > 0
                                                ? group.onWorkList.map(renderEmployeeName)
                                                : <div>-</div>}
                                        </td>
                                        <td>
                                            {group.offWorkList.length > 0
                                                ? group.offWorkList.map(renderEmployeeName)
                                                : <div>-</div>}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">근무 정보가 없습니다.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <footer className="summary-footer">
                    <p>{`출근자 ${summary.onWorkCount}명`}</p>
                    <p>{`휴무자 ${summary.offWorkCount}명`}</p>
                    <p>{`총인원 ${summary.totalCount}명`}</p>
                </footer>
            </div>

            <Link to="/" className="back-link">전체 스케줄로 돌아가기</Link>
        </div>
    );
}

export default DailySummary;
